import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiFetch } from './useApiFetch';
import type { EvalSetStatus, EvalSetStatusResponse } from '../types/monitoring';
import { isTerminal } from '../types/monitoring';
import { HttpError } from '../utils/httpError';
import { readSseStream } from '../utils/sse';

export type UseEvalSetStatusResult =
  | { state: 'loading' }
  | { state: 'error'; message: string }
  | { state: 'ready'; data: EvalSetStatus };

interface UseEvalSetStatusOptions {
  evalSetId: string | null;
  pollMs?: number;
}

function queryKey(evalSetId: string | null) {
  return ['eval-set-status', evalSetId] as const;
}

/**
 * Live status for an eval set, via SSE with a polling fallback.
 *
 * - An SSE stream pushes status as it changes; while it's healthy, polling is
 *   suspended. If the stream fails (or never connects), polling takes over
 *   automatically so the view always degrades safely.
 * - TanStack Query owns the data: the initial fetch + fallback poll run through
 *   `queryFn`, and stream messages are written via `setQueryData`.
 * - ETag / If-None-Match: a 304 returns the last payload (kept per eval set) so
 *   the server can skip re-sending unchanged status during a live run.
 * - 4xx stops polling (no point retrying an auth/not-found error); transient
 *   5xx/network errors keep the last good data on screen and keep polling.
 */
export function useEvalSetStatus({
  evalSetId,
  pollMs = 4000,
}: UseEvalSetStatusOptions): UseEvalSetStatusResult {
  const { apiFetch } = useApiFetch();
  const queryClient = useQueryClient();
  // Keyed by evalSetId so a 304 (or stream reconnect) never serves another eval
  // set's payload when the same hook instance switches IDs.
  const etagById = useRef<Map<string, string>>(new Map());
  const lastDataById = useRef<Map<string, EvalSetStatus>>(new Map());
  // True while an SSE stream is actively delivering updates; suspends polling.
  const [streaming, setStreaming] = useState(false);

  const query = useQuery<EvalSetStatus>({
    queryKey: queryKey(evalSetId),
    enabled: Boolean(evalSetId),
    refetchOnWindowFocus: false,
    retry: false,
    refetchInterval: q => {
      const data = q.state.data;
      if (data && isTerminal(data.status)) return false;
      if (streaming) return false; // stream is pushing; don't also poll
      if (q.state.error instanceof HttpError && q.state.error.status < 500) {
        return false; // 4xx: stop polling
      }
      return pollMs;
    },
    queryFn: async ({ signal }): Promise<EvalSetStatus> => {
      const id = evalSetId ?? '';
      const headers: Record<string, string> = {};
      const etag = etagById.current.get(id);
      if (etag) headers['If-None-Match'] = etag;
      const response = await apiFetch(
        `/monitoring/jobs/${encodeURIComponent(id)}/eval-set-status`,
        { headers, signal },
        { returnNonOkResponse: true }
      );
      if (!response) throw new Error('Network error');
      if (response.status === 304) {
        const cached = lastDataById.current.get(id);
        if (cached) return cached;
        throw new Error('Unexpected 304 with no cached data');
      }
      if (!response.ok) {
        throw new HttpError(
          `Request failed (${response.status})`,
          response.status
        );
      }
      const body = (await response.json()) as EvalSetStatusResponse;
      etagById.current.set(id, body.etag);
      lastDataById.current.set(id, body.data);
      return body.data;
    },
  });

  // Open an SSE stream for live push. On any failure we simply return and let
  // the poll fallback (above) keep the view fresh.
  useEffect(() => {
    const id = evalSetId;
    if (!id) return;
    const existing = queryClient.getQueryData<EvalSetStatus>(queryKey(id));
    if (existing && isTerminal(existing.status)) return; // nothing to stream

    const controller = new AbortController();
    let active = true;

    void (async () => {
      try {
        const response = await apiFetch(
          `/monitoring/jobs/${encodeURIComponent(id)}/eval-set-status/stream`,
          {
            headers: { Accept: 'text/event-stream' },
            signal: controller.signal,
          },
          { returnNonOkResponse: true }
        );
        if (!response || !response.ok || !response.body) return;
        // Streaming starts at connect, not at the first status frame: a
        // stuck-but-not-terminal run only sends keepalives, and polling in
        // parallel with a healthy stream defeats the point of the stream.
        setStreaming(true);
        for await (const frame of readSseStream(
          response.body,
          controller.signal
        )) {
          if (!active || frame.event !== 'status') continue;
          const body = JSON.parse(frame.data) as EvalSetStatusResponse;
          etagById.current.set(id, body.etag);
          lastDataById.current.set(id, body.data);
          queryClient.setQueryData(queryKey(id), body.data);
          if (isTerminal(body.data.status)) break;
        }
      } catch (err) {
        // Keep the polling fallback, but make the failure observable — a
        // permanently-broken stream silently degrading to polling forever is
        // exactly the kind of regression nobody would notice otherwise.
        if ((err as Error | null)?.name !== 'AbortError') {
          console.warn('eval-set status SSE stream failed', err);
        }
      } finally {
        if (active) setStreaming(false);
      }
    })();

    return () => {
      active = false;
      controller.abort();
      setStreaming(false);
    };
  }, [evalSetId, apiFetch, queryClient]);

  // Prefer last good data even while a later poll is erroring transiently.
  if (query.data) return { state: 'ready', data: query.data };
  if (query.error) return { state: 'error', message: query.error.message };
  return { state: 'loading' };
}
